import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useCart } from '../contexts/CartContext';

export default function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [item, setItem] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api(`/api/items/${id}`, {});
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Error ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) {
          setItem(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const handleCarouselNext = () => {
    if (!item || !item.imageUrls || item.imageUrls.length === 0) return;
    setCurrentImageIndex((prev) => 
      prev < item.imageUrls.length - 1 ? prev + 1 : 0
    );
  };

  const handleCarouselPrev = () => {
    if (!item || !item.imageUrls || item.imageUrls.length === 0) return;
    setCurrentImageIndex((prev) => 
      prev > 0 ? prev - 1 : item.imageUrls.length - 1
    );
  };

  const handleAddToCart = () => {
    if (isOutOfStock) return;
    addItem(id, 1);
  };

  const handleFacebookShare = async () => {
    if (!item) return;
    
    try {
      // Create item details text
      const itemDetails = `${item.title}\n\nPrice: $${parseFloat(item.price).toFixed(2)}\n\n${item.description || 'No description available.'}`;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(itemDetails);
      
      // Open Facebook Marketplace create page
      window.open('https://www.facebook.com/marketplace/create/item', '_blank');
      
      // Show message
      alert('Details copied! Paste into Facebook Marketplace.');
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      // Fallback: just open Facebook Marketplace
      window.open('https://www.facebook.com/marketplace/create/item', '_blank');
      alert('Please copy the item details manually and paste into Facebook Marketplace.');
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-2xl text-slate-500 dark:text-slate-400">Loading item…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-3xl font-semibold">Item Details</h1>
        <p className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">
          {error}
        </p>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-3xl font-semibold">Item Details</h1>
        <p className="text-lg text-slate-600 dark:text-slate-400">
          Item not found.
        </p>
      </div>
    );
  }

  const imageUrls = item.imageUrls || [];
  const isOutOfStock = item.stockCount <= 0;
  const hasImages = imageUrls.length > 0;

  return (
    <div className="flex flex-col gap-6 pb-40">
      {/* Image Carousel - Large at top, scales to window and shows full image */}
      <div className="w-full">
        <div className="relative w-full min-h-[200px] max-h-[70vh] bg-slate-100 dark:bg-slate-900 rounded-xl overflow-hidden flex items-center justify-center">
          {hasImages ? (
            <>
              <img
                src={imageUrls[currentImageIndex]}
                alt={item.title}
                className="max-w-full max-h-[70vh] w-auto h-auto object-contain"
              />
              {imageUrls.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={handleCarouselPrev}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors text-2xl"
                    aria-label="Previous image"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={handleCarouselNext}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors text-2xl"
                    aria-label="Next image"
                  >
                    ›
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {imageUrls.map((_, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`w-3 h-3 rounded-full transition-colors ${
                          idx === currentImageIndex
                            ? 'bg-white'
                            : 'bg-white/50 hover:bg-white/75'
                        }`}
                        aria-label={`Go to image ${idx + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="w-full min-h-[200px] flex items-center justify-center text-slate-400 text-xl">
              No image available
            </div>
          )}
        </div>
      </div>

      {/* Content Section */}
      <div className="flex flex-col gap-4">
        {/* Title */}
        <h1 className="text-3xl md:text-4xl font-bold">{item.title}</h1>

        {/* Price and Stock Badge */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-4xl font-bold text-indigo-600 dark:text-indigo-400">
            ${parseFloat(item.price).toFixed(2)}
          </div>
          {isOutOfStock ? (
            <span className="px-4 py-2 rounded-full bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-lg font-semibold w-fit">
              Out of Stock
            </span>
          ) : (
            <span className="px-4 py-2 rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-lg font-semibold w-fit">
              In Stock ({item.stockCount})
            </span>
          )}
        </div>

        {/* Vendor / Garage sale link — API returns garageSellerId and vendorName */}
        {(item.vendorName || item.garageSellerId) && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 dark:text-slate-400">Sold by:</span>
            <button
              type="button"
              onClick={() => navigate(`/vendor/${item.garageSellerId}`)}
              className="text-lg font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              {item.vendorName ? `${item.vendorName} Garage sale` : 'Garage sale'}
            </button>
          </div>
        )}

        {/* Description */}
        {item.description && (
          <div className="flex flex-col gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
            <h2 className="text-xl font-semibold">Description</h2>
            <p className="text-lg text-slate-700 dark:text-slate-300 whitespace-pre-line">
              {item.description}
            </p>
          </div>
        )}
      </div>

      {/* Fixed Add to Cart and Facebook Share Buttons at Bottom - Above BottomNav */}
      <div className="fixed bottom-20 left-0 right-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 p-4 shadow-lg z-40">
        <div className="max-w-screen-md mx-auto flex flex-col gap-3">
          <button
            type="button"
            onClick={handleAddToCart}
            disabled={isOutOfStock}
            className={`w-full py-4 rounded-xl text-xl font-semibold transition-colors ${
              isOutOfStock
                ? 'bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800'
            }`}
          >
            {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
          </button>
          <button
            type="button"
            onClick={handleFacebookShare}
            className="w-full py-3 rounded-xl text-lg font-semibold bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Share on Facebook
          </button>
        </div>
      </div>
    </div>
  );
}
